/**
 * F-233: the bridge between the SDK transport and the screens for the 2FA
 * refusals that any call can meet. No React, no SDK import: `internal.ts`
 * reads it on every failed call.
 *
 * - 403 `STEP_UP_REQUIRED` (turning 2FA off, changing its phone, new recovery
 *   codes, creating an API key, requesting a payout, the payout account): the
 *   transport asks `requestStepUpToken()`, which the mounted `StepUpHost`
 *   answers by opening the code modal, and repeats the call once with
 *   `X-Step-Up-Token`. The token lives 10 minutes, so a second protected
 *   action in that window reuses it instead of asking again.
 * - 403 `SUPPORT_SESSION_FORBIDDEN` (the SuperAdmin's "Entrar" session): no
 *   modal; the event lets the app say why, and the caller gets the error.
 * - 403 `TWO_FACTOR_REQUIRED` (the team past its 7 days without 2FA): the event
 *   turns the warning banner into the blocking one.
 */

export const STEP_UP_HEADER = 'X-Step-Up-Token'
export const TWO_FACTOR_REQUIRED_EVENT = 'memberfy:two-factor-required'
export const SUPPORT_SESSION_FORBIDDEN_EVENT = 'memberfy:support-session-forbidden'

/** The refusal codes the API puts at the top of a 403 body (`{ code, data }`). */
export const refusalCodeOf = (body: unknown): string | null => {
  const code = (body as { code?: unknown } | null)?.code
  return typeof code === 'string' ? code : null
}

type StepUpHandler = () => Promise<{ token: string; expiresAt: string } | null>

/**
 * Who a step-up token was issued to: the session token, the community and the
 * "act as" member the request carried. A token is only reused (or a pending
 * modal shared) for the same scope, so after a logout, another person's login
 * or a community switch in this tab the previous token is never sent.
 */
export type StepUpScope = string

let handler: StepUpHandler | null = null
let cached: { token: string; expiresAt: number; scope: StepUpScope } | null = null
let pending: { promise: Promise<string | null>; scope: StepUpScope } | null = null

/** Mounted once by `StepUpHost`; `null` when it unmounts. */
export const setStepUpHandler = (next: StepUpHandler | null) => { handler = next }

/** Builds the scope from what the request carries; any missing part is just empty. */
export const stepUpScopeOf = (parts: { token?: unknown; communityId?: unknown; memberId?: unknown }): StepUpScope =>
  [parts.token, parts.communityId, parts.memberId].map((part) => (typeof part === 'string' ? part : '')).join('|')

/** A token still valid for at least 15 seconds, if this tab has one for this scope. */
export const cachedStepUpToken = (scope: StepUpScope): string | null =>
  cached && cached.scope === scope && cached.expiresAt - Date.now() > 15_000 ? cached.token : null

/** The cached token did not hold (the password changed, or it expired on the server): ask again next time. */
export const dropStepUpToken = () => { cached = null }

/**
 * Forgets the cached token and any modal in flight. Called on logout, login,
 * the session ending and a community switch; an answer still pending is
 * discarded instead of cached.
 */
export const clearStepUpState = () => {
  cached = null
  pending = null
}

/**
 * A fresh token from the person (the modal), or `null` when they gave up or
 * nothing can ask. Two calls at once for the same scope share one modal.
 */
export const requestStepUpToken = (scope: StepUpScope): Promise<string | null> => {
  if (!handler) return Promise.resolve(null)
  if (pending && pending.scope === scope) return pending.promise
  const ask = handler
  const entry: { promise: Promise<string | null>; scope: StepUpScope } = { scope, promise: Promise.resolve(null) }
  entry.promise = ask()
    .then((answer) => {
      /* The state was cleared (or another scope took over) while the modal was open: do not keep it. */
      if (!answer || pending !== entry) return null
      const until = Date.parse(answer.expiresAt)
      cached = { token: answer.token, expiresAt: Number.isFinite(until) ? until : Date.now() + 9 * 60_000, scope }
      return answer.token
    })
    .catch(() => null)
    .finally(() => { if (pending === entry) pending = null })
  pending = entry
  return entry.promise
}

/** Every failed call passes here: the refusals that are not the step-up only raise their events. */
export const announceRefusal = (status: number | undefined, body: unknown) => {
  if (status !== 403) return
  const code = refusalCodeOf(body)
  if (code === 'TWO_FACTOR_REQUIRED') {
    window.dispatchEvent(new CustomEvent(TWO_FACTOR_REQUIRED_EVENT, { detail: (body as { data?: unknown })?.data ?? null }))
  } else if (code === 'SUPPORT_SESSION_FORBIDDEN') {
    window.dispatchEvent(new CustomEvent(SUPPORT_SESSION_FORBIDDEN_EVENT, { detail: (body as { message?: string })?.message ?? '' }))
  }
}
